import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { MAX_VARIANTS_PER_LANGUAGE } from '../constants';
import { OfferAudioUploadState, OfferConfig, OfferLanguageEntry } from '../types';
import { getVariantStateKey, isLanguageDirty } from '../utils';
import VariantCard from './VariantCard';

type UploadStateMap = Record<string, OfferAudioUploadState>;

type VariantEditorProps = {
    offer: OfferConfig;
    snapshotOffer: OfferConfig | null;
    language: OfferLanguageEntry | null;
    languageIndex: number;
    canEdit: boolean;
    uploadStateMap: UploadStateMap;
    onBoxNameChange: (variantIndex: number, value: string) => void;
    onPackageIdChange: (variantIndex: number, value: string) => void;
    onFileUpload: (variantIndex: number, file: File) => void;
    onAddVariant: () => void;
    onRemoveVariant: (variantIndex: number) => void;
};

export default function VariantEditor({
    offer,
    snapshotOffer,
    language,
    languageIndex,
    canEdit,
    uploadStateMap,
    onBoxNameChange,
    onPackageIdChange,
    onFileUpload,
    onAddVariant,
    onRemoveVariant,
}: VariantEditorProps) {
    if (!language) {
        return (
            <div className="oz-offer-language-config-editor">
                <Box className="oz-offer-language-config-panel" sx={{ padding: '16px' }}>
                    <div className="oz-offer-language-config-empty">
                        <Typography sx={{ fontSize: '13px', lineHeight: '18px' }}>
                            Select a language to configure variants.
                        </Typography>
                    </div>
                </Box>
            </div>
        );
    }

    const languageDirty = isLanguageDirty(offer, snapshotOffer, languageIndex);
    const canAddMore = language.variants.length < MAX_VARIANTS_PER_LANGUAGE;

    return (
        <div className="oz-offer-language-config-editor">
            <Box className="oz-offer-language-config-panel" sx={{ padding: '16px' }}>
                <div className="oz-offer-language-config-editor-header">
                    <Box>
                        <Typography sx={{ color: '#0f172a', fontSize: '18px', fontWeight: 500 }}>
                            {language.language_name}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '13px', lineHeight: '18px', marginTop: '4px' }}>
                            Update package IDs and upload processed audio for each box.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        {languageDirty && <Chip label="Unsaved edits" color="warning" variant="outlined" />}
                        {!canEdit && <Chip label="View only" variant="outlined" />}
                        {canEdit && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddCircleOutlineIcon />}
                                disabled={!canAddMore}
                                onClick={onAddVariant}
                                sx={{ borderRadius: '10px' }}
                            >
                                Add Box ({language.variants.length}/{MAX_VARIANTS_PER_LANGUAGE})
                            </Button>
                        )}
                    </Stack>
                </div>

                {language.variants.length === 0 ? (
                    <div className="oz-offer-language-config-empty">
                        <Typography sx={{ fontSize: '13px', lineHeight: '18px' }}>
                            No boxes configured. Click "Add Box" to add one.
                        </Typography>
                    </div>
                ) : (
                    <div className="oz-offer-language-config-variant-grid">
                        {language.variants.map((variant, variantIndex) => {
                            const stateKey = getVariantStateKey(offer.offer_name, language.language_name, variant.id);
                            return (
                                <VariantCard
                                    key={variant.id}
                                    variant={variant}
                                    uploadState={uploadStateMap[stateKey]}
                                    canEdit={canEdit}
                                    stateKey={stateKey}
                                    onBoxNameChange={(value) => onBoxNameChange(variantIndex, value)}
                                    onPackageIdChange={(value) => onPackageIdChange(variantIndex, value)}
                                    onFileUpload={(file) => onFileUpload(variantIndex, file)}
                                    onRemove={() => onRemoveVariant(variantIndex)}
                                />
                            );
                        })}
                    </div>
                )}
            </Box>
        </div>
    );
}
